import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import '../pages/TransactionPage.css';
import '../styles/MoneyMarketDealPage.css';
import { calculateInterestAmount } from '../services/interestCalculator';
import FrontOfficeMoneyMarketBlotter from './FrontOfficeMoneyMarketBlotter';
import BackOfficeMoneyMarketBlotter from './BackOfficeMoneyMarketBlotter';
import BackOfficeFinalMoneyMarketBlotter from './BackOfficeFinalMoneyMarketBlotter';
import { API_BASE_URL, moneyMarketApi } from '../services/api';
import { fetchPaymentMethods } from '../services/paymentApi';
import { DATE_OBJ_MIN, DATE_OBJ_MAX } from '../utils/dateBounds';
import { checkHolidayForCurrency, formatHolidayMessage } from '../utils/holidayValidation';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';

function pad(num, size) {
  let s = num + '';
  while (s.length < size) s = '0' + s;
  return s;
}

function generateDealNumber(date, serial) {
  const yyyyMMdd = date.toISOString().slice(0, 10).replace(/-/g, '');
  return `${yyyyMMdd}MMAR${pad(serial, 4)}`;
}

const productTypes = ['Call Money', 'Notice Money', 'Term Money'];
const currencies = ['INR', 'USD', 'LKR'];
// Settlement modes will be fetched from payment_masters as payment method codes
const dealTypes = ['Lending', 'Borrowing'];

const MoneyMarketDealPage = () => {
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user?.role || '';
  
  // Support all authorizer roles for multi-level auth
  const AUTHORIZER_ROLES = ['front_office', 'front_office_verifier', 'back_office_verifier', 'back_office_final'];
  const isAuthorizer = user && AUTHORIZER_ROLES.includes(userRole);
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentCode, setSelectedPaymentCode] = useState('');
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);

  // Check for reinvestment data from maturity handling
  const getReinvestmentData = () => {
    const reinvestmentData = localStorage.getItem('reinvestmentDealData');
    if (reinvestmentData) {
      try {
        const data = JSON.parse(reinvestmentData);
        localStorage.removeItem('reinvestmentDealData'); // Clear after use
        return data;
      } catch (error) {
        console.error('Error parsing reinvestment data:', error);
      }
    }
    return null;
  };

  const reinvestmentData = getReinvestmentData();

  const [tradeDate, setTradeDate] = useState(new Date());
  const [valueDate, setValueDate] = useState(new Date());
  const [maturityDate, setMaturityDate] = useState(new Date());
  const [counterparties, setCounterparties] = useState([]);
  const [counterparty, setCounterparty] = useState('');
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [productType, setProductType] = useState(productTypes[0]);
  // LKR is the house currency - default to it rather than currencies[0],
  // which was 'INR'.
  const [currency, setCurrency] = useState('LKR');
  const [principal, setPrincipal] = useState(reinvestmentData?.principalAmount || '');
  const [interestRate, setInterestRate] = useState('');
  const [settlementMode, setSettlementMode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [serial, setSerial] = useState(1);
  const [dealType, setDealType] = useState(dealTypes[0]);
  const [editingDeal, setEditingDeal] = useState(null); // Track if we're editing a deal
  const [rejectedDeals, setRejectedDeals] = useState([]); // Store rejected deals for the user
  const [createdMoneyMarketDealNumber, setCreatedMoneyMarketDealNumber] = useState(null);
  const [showMoneyMarketDocumentSection, setShowMoneyMarketDocumentSection] = useState(false);

  // For demo, serial resets on tradeDate change. In production, fetch from backend.
  useEffect(() => {
    setSerial(1); // Reset serial on trade date change
  }, [tradeDate]);

  useEffect(() => {
    setCounterparties([]);
    setLoadingCounterparties(true);
    axios.get(`${API_BASE_URL}/counterparties`)
      .then(res => {
        const counterpartyData = res.data.data || [];
        setCounterparties(counterpartyData);
      })
      .catch(err => {
        setCounterparties([]);
      })
      .finally(() => setLoadingCounterparties(false));
  }, []);

  // Fetch settlement modes from payment_masters
  useEffect(() => {
    fetchPaymentMethods().then(data => setPaymentMethods(data || []));
  }, []);

  // Fetch rejected deals for the current user
  useEffect(() => {
    if (!isAuthorizer && user?.id) {
      moneyMarketApi.getAll().then(allDeals => {
        const rejected = (allDeals || []).filter(deal => 
          deal.status === 'rejected' && deal.created_by === user.id
        );
        setRejectedDeals(rejected);
      });
    }
  }, [isAuthorizer, user?.id]);

  // Re-validate dates when currency changes
  useEffect(() => {
    const currentCurrency = currency || 'LKR';
    
    if (tradeDate) {
      const formattedDate = tradeDate.toISOString().split('T')[0];
      checkHolidayForCurrency(formattedDate, currentCurrency).then(holidayCheck => {
        if (holidayCheck.isHoliday) {
          const message = formatHolidayMessage(holidayCheck, 'Trade Date') || holidayCheck.message || `Trade Date (${formattedDate}) is a holiday. Transactions cannot be saved on holidays.`;
          setTradeDateHolidayError(message);
        } else {
          setTradeDateHolidayError('');
        }
      }).catch(err => {
        console.error('Error checking holiday:', err);
        setTradeDateHolidayError('');
      });
    }
    
    if (valueDate) {
      const formattedDate = valueDate.toISOString().split('T')[0];
      checkHolidayForCurrency(formattedDate, currentCurrency).then(holidayCheck => {
        if (holidayCheck.isHoliday) {
          const message = formatHolidayMessage(holidayCheck, 'Value Date') || holidayCheck.message || `Value Date (${formattedDate}) is a holiday. Transactions cannot be saved on holidays.`;
          setValueDateHolidayError(message);
        } else {
          setValueDateHolidayError('');
        }
      }).catch(err => {
        console.error('Error checking holiday:', err);
        setValueDateHolidayError('');
      });
    }
    
    if (maturityDate) {
      const formattedDate = maturityDate.toISOString().split('T')[0];
      checkHolidayForCurrency(formattedDate, currentCurrency).then(holidayCheck => {
        if (holidayCheck.isHoliday) {
          const message = formatHolidayMessage(holidayCheck, 'Maturity Date') || holidayCheck.message || `Maturity Date (${formattedDate}) is a holiday. Transactions cannot be saved on holidays.`;
          setMaturityDateHolidayError(message);
        } else {
          setMaturityDateHolidayError('');
        }
      }).catch(err => {
        console.error('Error checking holiday:', err);
        setMaturityDateHolidayError('');
      });
    }
  }, [currency, tradeDate, valueDate, maturityDate]);

  const handleSettlementModeChange = (e) => {
    const code = e.target.value;
    setSettlementMode(code);
    const details = paymentMethods.find(pm => pm.payment_method_code === code);
    setSelectedPaymentDetails(details || null);
  };

  const dealNumber = generateDealNumber(tradeDate, serial);

  // Calculate tenor (days between value and maturity date)
  const tenor = valueDate && maturityDate ? Math.max(0, Math.round((maturityDate - valueDate) / (1000 * 60 * 60 * 24))) : 0;

  // Calculate interest amount using utility function
  const principalNum = parseFloat(principal) || 0;
  const interestRateNum = parseFloat(interestRate) || 0;
  const interestAmountNum = calculateInterestAmount(principalNum, interestRateNum, tenor);
  const interestAmount = interestAmountNum.toFixed(4);
  const maturityValue = (principalNum + interestAmountNum).toFixed(4);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // State for holiday validation errors
  const [tradeDateHolidayError, setTradeDateHolidayError] = useState('');
  const [valueDateHolidayError, setValueDateHolidayError] = useState('');
  const [maturityDateHolidayError, setMaturityDateHolidayError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const dealData = {
        dealNumber: editingDeal ? editingDeal.deal_number : dealNumber,
        tradeDate: tradeDate.toISOString().slice(0, 10),
        valueDate: valueDate.toISOString().slice(0, 10),
        maturityDate: maturityDate.toISOString().slice(0, 10),
        counterpartyId: counterparty,
        productType,
        currency,
        principalAmount: principalNum,
        interestRate: interestRateNum,
        tenor,
        interestAmount: interestAmountNum,
        maturityValue: (principalNum + interestAmountNum),
        settlementMode,
        remarks,
        dealType,
        userId: user?.id || null,
        username: user?.username || null
      };

      if (editingDeal) {
        // Update existing deal (rejected deal being edited)
        await moneyMarketApi.update(editingDeal.deal_number, {
          ...dealData,
          status: 'pending', // Change status back to pending when resubmitted
          current_approval_level: 'front_office'
        });
        setSaveSuccess('Deal updated and resubmitted successfully!');
        // Refresh rejected deals list
        const allDeals = await moneyMarketApi.getAll();
        const rejected = (allDeals || []).filter(deal => 
          deal.status === 'rejected' && deal.created_by === user.id
        );
        setRejectedDeals(rejected);
      } else {
        // Create new deal
        const response = await axios.post(`${API_BASE_URL}/money-market-deals`, dealData);
        const createdDealNumber = response.data?.data?.deal_number || dealData.dealNumber;
        setCreatedMoneyMarketDealNumber(createdDealNumber);
        setShowMoneyMarketDocumentSection(true);
        setSaveSuccess('Deal saved successfully!');
      }
      
      // Reset form
      setPrincipal('');
      setInterestRate('');
      setCounterparty('');
      setRemarks('');
      setEditingDeal(null);
      setTradeDate(new Date());
      setValueDate(new Date());
      setMaturityDate(new Date());
      // Optionally reset other fields
    } catch (err) {
      setSaveError('Failed to save deal: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (deal) => {
    // Populate form with deal data
    setEditingDeal(deal);
    setTradeDate(new Date(deal.trade_date));
    setValueDate(new Date(deal.value_date));
    setMaturityDate(new Date(deal.maturity_date));
    setCounterparty(deal.counterparty_id);
    setProductType(deal.product_type);
    setCurrency(deal.currency);
    setPrincipal(deal.principal_amount?.toString() || '');
    setInterestRate(deal.interest_rate?.toString() || '');
    setSettlementMode(deal.settlement_mode || '');
    setRemarks(deal.remarks || '');
    setDealType(deal.deal_type || dealTypes[0]);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // For authorizers, show the appropriate blotter instead of the entry form
  if (isAuthorizer) {
    if (userRole === 'back_office_final') {
      return <BackOfficeFinalMoneyMarketBlotter />;
    } else if (userRole === 'back_office_verifier') {
      return <BackOfficeMoneyMarketBlotter />;
    } else if (userRole === 'front_office_verifier' || userRole === 'front_office') {
      return <FrontOfficeMoneyMarketBlotter />;
    }
  }

  return (
    <div className="page-container ui-page">
      {/* Show rejected deals for the user */}
      {!isAuthorizer && rejectedDeals.length > 0 && (
        <div className="transaction-form-container" style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ marginTop: 0, color: 'var(--warning-dark)', marginBottom: 'var(--space-3)' }}>Rejected Deals - Please Review and Edit</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="transactions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--warning)', color: 'var(--warning-dark)' }}>
                  <th style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left' }}>Deal Number</th>
                  <th style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left' }}>Trade Date</th>
                  <th style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left' }}>Principal</th>
                  <th style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left' }}>Rejection Reason</th>
                  <th style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rejectedDeals.map(deal => (
                  <tr key={deal.deal_number} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>{deal.deal_number}</td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>{deal.trade_date ? new Date(deal.trade_date).toLocaleDateString() : ''}</td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>{deal.principal_amount} {deal.currency}</td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--danger)' }}>{deal.comment || 'No reason provided'}</td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                      <button type="button" onClick={() => handleEdit(deal)} className="action-button edit-button">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="transaction-form-container">
        <form className="transaction-form money-market-deal-form" onSubmit={handleSave}>

          {selectedPaymentDetails && (
            <div className="form-grid payment-details-row" style={{ gridColumn: '1 / -1', background: 'var(--bg-subtle)', padding: 'var(--space-4)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: 'var(--space-4)' }}>
              <div><strong style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Bank Name</strong><div>{selectedPaymentDetails.bank_name}</div></div>
              <div><strong style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Branch</strong><div>{selectedPaymentDetails.branch}</div></div>
              <div><strong style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Account Number</strong><div>{selectedPaymentDetails.account_number}</div></div>
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Deal Number</label>
              <input type="text" className="form-control" value={editingDeal ? editingDeal.deal_number : dealNumber} readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Deal Type</label>
              <select className="form-control" value={dealType} onChange={e => setDealType(e.target.value)}>
                {dealTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Trade Date</label>
              <DatePicker
                selected={tradeDate}
                onChangeRaw={(e) => e.preventDefault()}
                onChange={date => {
                  setTradeDate(date);
                  if (date) {
                    const formattedDate = date.toISOString().split('T')[0];
                    const currentCurrency = currency || 'LKR';
                    checkHolidayForCurrency(formattedDate, currentCurrency).then(holidayCheck => {
                      if (holidayCheck.isHoliday) {
                        const message = formatHolidayMessage(holidayCheck, 'Trade Date') || holidayCheck.message || `Trade Date (${formattedDate}) is a holiday. Transactions cannot be saved on holidays.`;
                        setTradeDateHolidayError(message);
                      } else {
                        setTradeDateHolidayError('');
                      }
                    }).catch(err => {
                      console.error('Error checking holiday:', err);
                      setTradeDateHolidayError('');
                    });
                  } else {
                    setTradeDateHolidayError('');
                  }
                }}
                dateFormat="yyyy-MM-dd"
                minDate={DATE_OBJ_MIN}
                maxDate={DATE_OBJ_MAX}
                className={`form-control react-datepicker-input ${tradeDateHolidayError ? 'is-invalid' : ''}`}
              />
              {tradeDateHolidayError && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{tradeDateHolidayError}</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Value Date</label>
              <DatePicker
                selected={valueDate}
                onChangeRaw={(e) => e.preventDefault()}
                onChange={date => {
                  setValueDate(date);
                  if (date) {
                    const formattedDate = date.toISOString().split('T')[0];
                    const currentCurrency = currency || 'LKR';
                    checkHolidayForCurrency(formattedDate, currentCurrency).then(holidayCheck => {
                      if (holidayCheck.isHoliday) {
                        const message = formatHolidayMessage(holidayCheck, 'Value Date') || holidayCheck.message || `Value Date (${formattedDate}) is a holiday. Transactions cannot be saved on holidays.`;
                        setValueDateHolidayError(message);
                      } else {
                        setValueDateHolidayError('');
                      }
                    }).catch(err => {
                      console.error('Error checking holiday:', err);
                      setValueDateHolidayError('');
                    });
                  } else {
                    setValueDateHolidayError('');
                  }
                }}
                dateFormat="yyyy-MM-dd"
                minDate={DATE_OBJ_MIN}
                maxDate={DATE_OBJ_MAX}
                className={`form-control react-datepicker-input ${valueDateHolidayError ? 'is-invalid' : ''}`}
              />
              {valueDateHolidayError && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{valueDateHolidayError}</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Maturity Date</label>
              <DatePicker
                selected={maturityDate}
                onChangeRaw={(e) => e.preventDefault()}
                onChange={date => {
                  setMaturityDate(date);
                  if (date) {
                    const formattedDate = date.toISOString().split('T')[0];
                    const currentCurrency = currency || 'LKR';
                    checkHolidayForCurrency(formattedDate, currentCurrency).then(holidayCheck => {
                      if (holidayCheck.isHoliday) {
                        const message = formatHolidayMessage(holidayCheck, 'Maturity Date') || holidayCheck.message || `Maturity Date (${formattedDate}) is a holiday. Transactions cannot be saved on holidays.`;
                        setMaturityDateHolidayError(message);
                      } else {
                        setMaturityDateHolidayError('');
                      }
                    }).catch(err => {
                      console.error('Error checking holiday:', err);
                      setMaturityDateHolidayError('');
                    });
                  } else {
                    setMaturityDateHolidayError('');
                  }
                }}
                dateFormat="yyyy-MM-dd"
                minDate={DATE_OBJ_MIN}
                maxDate={DATE_OBJ_MAX}
                className={`form-control react-datepicker-input ${maturityDateHolidayError ? 'is-invalid' : ''}`}
              />
              {maturityDateHolidayError && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{maturityDateHolidayError}</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Counterparty</label>
              <select
                className="form-control"
                name="counterparty"
                value={counterparty}
                onChange={e => setCounterparty(e.target.value)}
              >
                <option value="">Select Counterparty</option>
                {(() => {
                  const individual = counterparties.filter(cp => cp.type === 'individual');
                  const joint = counterparties.filter(cp => cp.type === 'joint');
                  const corporate = counterparties.filter(cp => cp.type === 'corporate');
                  return (
                    <>
                      {individual.length > 0 && (
                        <optgroup label="Individual" className="counterparty-individual">
                          {individual.map(cp => (
                            <option key={cp.unique_id} value={cp.unique_id} className="counterparty-individual">
                              {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {joint.length > 0 && (
                        <optgroup label="Joint" className="counterparty-joint">
                          {joint.map(cp => (
                            <option key={cp.unique_id} value={cp.unique_id} className="counterparty-joint">
                              {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {corporate.length > 0 && (
                        <optgroup label="Corporate" className="counterparty-corporate">
                          {corporate.map(cp => (
                            <option key={cp.unique_id} value={cp.unique_id} className="counterparty-corporate">
                              {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Product Type</label>
              <select className="form-control" value={productType} onChange={e => setProductType(e.target.value)}>
                {productTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-control" value={currency} onChange={e => setCurrency(e.target.value)}>
                {currencies.map(cur => (
                  <option key={cur} value={cur}>{cur}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Principal Amount</label>
              <input type="number" className="form-control" value={principal} onChange={e => setPrincipal(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Interest Rate (%)</label>
              <input type="number" className="form-control" value={interestRate} onChange={e => setInterestRate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Tenor (days)</label>
              <input type="text" className="form-control" value={tenor} readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Interest Amount</label>
              <input type="text" className="form-control" value={interestAmount} readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Maturity Value</label>
              <input type="text" className="form-control" value={maturityValue} readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Settlement Mode</label>
              <select className="form-control" value={settlementMode} onChange={handleSettlementModeChange}>
                <option value="">Select Settlement Mode</option>
                {paymentMethods.map(pm => (
                  <option key={pm.payment_method_code} value={pm.payment_method_code}>{pm.payment_method_code}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Remarks</label>
              <input type="text" className="form-control" value={remarks} onChange={e => setRemarks(e.target.value)} />
            </div>
          </div>

          <div className="form-row" style={{ marginTop: 'var(--space-6)', marginBottom: 0, gap: 'var(--space-3)' }}>
            <button type="submit" disabled={saving} className="save-verify-button">
              {saving ? 'Saving...' : editingDeal ? 'Update and Resubmit Deal' : 'Save Deal'}
            </button>
            {editingDeal && (
              <button
                type="button"
                onClick={() => {
                  setEditingDeal(null);
                  setPrincipal('');
                  setInterestRate('');
                  setCounterparty('');
                  setRemarks('');
                  setTradeDate(new Date());
                  setValueDate(new Date());
                  setMaturityDate(new Date());
                }}
                className="action-button cancel-btn"
              >
                Cancel Edit
              </button>
            )}
          </div>
          {saveError && <div className="alert alert-danger" style={{ marginTop: 'var(--space-4)' }}>{saveError}</div>}
          {saveSuccess && <div className="alert alert-success" style={{ marginTop: 'var(--space-4)' }}>{saveSuccess}</div>}
        </form>
      </div>
      
      {/* Document Upload Section - Show after deal is created or when viewing existing deal */}
      {(showMoneyMarketDocumentSection && createdMoneyMarketDealNumber) || (editingDeal && editingDeal.deal_number) ? (
        <div className="transaction-form-container" style={{ marginTop: 'var(--space-6)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
            <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              Documents
              {(createdMoneyMarketDealNumber || editingDeal?.deal_number) && (
                <span style={{ fontSize: '0.875rem', fontWeight: 'normal', color: 'var(--text-muted)', marginLeft: 'var(--space-2)' }}>
                  – Deal #: {createdMoneyMarketDealNumber || editingDeal?.deal_number}
                </span>
              )}
            </h4>
            {showMoneyMarketDocumentSection && (
              <button type="button" className="action-button cancel-btn" onClick={() => setShowMoneyMarketDocumentSection(false)}>
                Close
              </button>
            )}
          </div>
          <DocumentUpload
            transactionType="money_market"
            transactionId={createdMoneyMarketDealNumber || editingDeal?.deal_number}
            onUploadSuccess={(documents) => {
              setSaveSuccess('Document(s) uploaded successfully');
              if (!editingDeal) {
                setShowMoneyMarketDocumentSection(false);
              }
            }}
            onUploadError={(error) => {
              setSaveError('Failed to upload document: ' + (error.response?.data?.error || error.message));
            }}
            multiple={true}
          />
          <DocumentList
            transactionType="money_market"
            transactionId={createdMoneyMarketDealNumber || editingDeal?.deal_number}
            onDocumentDeleted={() => {}}
          />
        </div>
      ) : null}
    </div>
  );
};


export default MoneyMarketDealPage;
